import { maxDotNumber } from "@zyzgroup/core-common";
import { YZCanvasContext } from "../canvas";
import { type SVGParsedPathData } from "../../../svg/";
import { L, LOptions } from "./L";

export type ArcOptions = {
  rx: number; // x方向的半径
  ry: number; // y方向的半径
  xAxisRotate?: number; // phi 绕着x轴旋转的角度
  largeArcFlag?: number; // fa 0小弧 1大弧
  sweepFlag?: number; // fs 0逆时针方向 1顺时针方向
  toX: number;
  toY: number;
} & LOptions;

export class Arc extends L {
  public rx: number;
  public ry: number;
  public xAxisRotate: number;
  public largeArcFlag: number;
  public sweepFlag: number;

  constructor(options: ArcOptions) {
    super(options);
    this.rx = maxDotNumber(options.rx, 2);
    this.ry = maxDotNumber(options.ry, 2);
    this.xAxisRotate =
      typeof options?.xAxisRotate == "number" ? options?.xAxisRotate : 0;
    this.largeArcFlag =
      typeof options?.largeArcFlag == "number" ? options?.largeArcFlag : 1;
    this.sweepFlag =
      typeof options?.sweepFlag == "number" ? options?.sweepFlag : 1;
  }

  /// A rx,ry xAxisRotate LargeArcFlag,SweepFlag x,y
  public toSVGPathData(): SVGParsedPathData[] {
    return [
      [
        "A",
        this.rx,
        this.ry,
        this.xAxisRotate,
        this.largeArcFlag,
        this.sweepFlag,
        this.toX,
        this.toY
      ]
    ];
  }

  public toCanvasContext(ctx: YZCanvasContext): void {
    const [cx, cy, rx, ry, xAxisRotate, startAngle, endAngle] = this.convert(
      this.x,
      this.y,
      this.toX,
      this.toY,
      this.rx,
      this.ry,
      this.xAxisRotate,
      this.largeArcFlag,
      this.sweepFlag
    );
    ctx.ellipse(
      cx,
      cy,
      rx,
      ry,
      xAxisRotate,
      startAngle,
      endAngle,
      !this.sweepFlag
    );
    // ctx.scale(this.rx, this.ry);
    // ctx.arc(0, 0, 1, startAngle, endAngle, !this.sweepFlag);
  }

  private convert(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    rx: number,
    ry: number,
    phi: number,
    fa: number,
    fs: number
  ) {
    const p = (phi / 180) * Math.PI;
    const x_m = (x1 - x2) / 2;
    const y_m = (y1 - y2) / 2;

    const x1_d = Math.cos(p) * x_m + Math.sin(p) * y_m;
    const y1_d = -Math.sin(p) * x_m + Math.cos(p) * y_m;

    const radius_check_value =
      (x1_d * x1_d) / (rx * rx) + (y1_d * y1_d) / (ry * ry);
    if (radius_check_value > 1) {
      // throw "Radius is too small to build an arc!";

      // Check out radius correction in the W3C document
      // https://www.w3.org/TR/SVG11/implnote.html#ArcCorrectionOutOfRangeRadii
      const r_sq = Math.sqrt(radius_check_value);
      rx = r_sq * rx;
      ry = r_sq * ry;

      throw new Error(
        `Radii are too small to build an arc. Correcting them to ${rx}/${ry}.`
      );
    }

    const sq = Math.sqrt(
      (rx * rx * ry * ry - rx * rx * y1_d * y1_d - ry * ry * x1_d * x1_d) /
        (rx * rx * y1_d * y1_d + ry * ry * x1_d * x1_d)
    );

    const s = fa != fs ? 1 : -1;
    const cx_d = (s * sq * rx * y1_d) / ry;
    const cy_d = (s * sq * -ry * x1_d) / rx;

    const x_m_d = (x1 + x2) / 2;
    const y_m_d = (y1 + y2) / 2;
    const cx = Math.cos(p) * cx_d - Math.sin(p) * cy_d + x_m_d;
    const cy = Math.sin(p) * cx_d + Math.cos(p) * cy_d + y_m_d;

    const vectorAngle = (ux: number, uy: number, vx: number, vy: number) =>
      (ux * vy >= uy * vx ? 1 : -1) *
      Math.acos(
        (ux * vx + uy * vy) /
          (Math.sqrt(ux * ux + uy * uy) * Math.sqrt(vx * vx + vy * vy))
      );
    const theta_1 = vectorAngle(1, 0, x1 - cx, y1 - cy) - p;
    const delta_theta = vectorAngle(x1 - cx, y1 - cy, x2 - cx, y2 - cy);

    return [cx, cy, rx, ry, p, theta_1, theta_1 + delta_theta];
  }
}

export default Arc;
